import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { ButtonComponent } from '@/components/commons'
import {
  confirmationModalActions,
  confirmationModalContent,
  confirmationModalMessage,
  confirmationModalOverlay,
  confirmationModalTitle,
} from '@/components/confirmationModal/ConfirmationModal.css'

type ConfirmationModalProps = {
  message?: string
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  confirmButtonType?: 'alert' | 'default'
}

type ResolveRef = { resolve: (value: unknown) => void; reject: () => void }
type ModalResultRef = unknown

type ConfirmationModalContextType = {
  isOpen: boolean
  modalProps: Record<string, unknown>
  setIsOpen: (isOpen: boolean) => void
  setModalProps: (modalProps: Record<string, unknown>) => void
  promiseRef: React.MutableRefObject<ResolveRef>
  modalResultRef: React.MutableRefObject<ModalResultRef>
}
const ConfirmationModalContext = createContext(
  {} as ConfirmationModalContextType
)

export const ConfirmationModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalProps, setModalProps] = useState({})
  const promiseRef = useRef<ResolveRef>(null)
  const modalResultRef = useRef<ModalResultRef>(null)

  return (
    <ConfirmationModalContext.Provider
      value={{
        isOpen,
        modalProps,
        setModalProps,
        setIsOpen,
        promiseRef,
        modalResultRef,
      }}
    >
      <ConfirmationModalRoot>
        <ConfirmationModalContent />
      </ConfirmationModalRoot>
      {children}
    </ConfirmationModalContext.Provider>
  )
}

export const ConfirmationModalRoot = ({ children }) => {
  const { isOpen, setIsOpen, promiseRef, modalProps, modalResultRef } =
    useContext(ConfirmationModalContext)
  const isMounted = useRef(false)
  const projectRoot = useRef<HTMLElement>(null)

  useEffect(() => {
    const modalResult = modalResultRef.current
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    if (!isOpen && promiseRef.current) {
      promiseRef.current.resolve(modalResult)
    }
    if (!isOpen) {
      promiseRef.current = null
      modalResultRef.current = null
    }
  }, [isOpen, modalResultRef, promiseRef])
  const onOpenChange = useCallback(
    (open) => {
      setIsOpen(open)
    },
    [setIsOpen]
  )
  const childrenWithProps = useMemo(() => {
    if (React.Children.count(children) === 1 && modalProps) {
      return React.Children.map(children, (child) => {
        if (React.isValidElement(child))
          return React.cloneElement(child, modalProps)
        return child
      })
    }
    return children
  }, [modalProps, children])

  // mount the dialog in the actual root so that we can access fonts and css variables
  useEffect(() => {
    projectRoot.current = document.getElementById('creatorland-root')
  }, [])

  // Prevent modal from closing on escape
  const onEscapeKeyDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    return false
  }, [])
  const onPointerDownOutside = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    return false
  }, [])
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal container={projectRoot.current}>
        <Dialog.Overlay className={confirmationModalOverlay} />
        <Dialog.Content
          onPointerDownOutside={onPointerDownOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          className={confirmationModalContent}
        >
          {childrenWithProps}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export const useConfirmationModal = (props?: ConfirmationModalProps) => {
  const { setIsOpen, setModalProps, promiseRef } = useContext(
    ConfirmationModalContext
  )

  return useCallback(
    (callbackProps?: ConfirmationModalProps): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        setModalProps(props || callbackProps)
        promiseRef.current = { resolve, reject }
        setIsOpen(true)
      })
    },
    [promiseRef, setIsOpen, setModalProps, props]
  )
}

const ConfirmationModalContent = (props: ConfirmationModalProps) => {
  const {
    message = 'Are you sure that you want to perform this action?',
    title = 'Confirm Action',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
  } = props
  const { setIsOpen, modalResultRef, promiseRef } = useContext(
    ConfirmationModalContext
  )

  const hideModal = useCallback(
    (result: boolean) => {
      modalResultRef.current = result
      if (promiseRef.current) {
        promiseRef.current.resolve(result)
      }
      promiseRef.current = null
      setIsOpen(false)
    },
    [modalResultRef, promiseRef, setIsOpen]
  )

  const onConfirm = useCallback(() => {
    return hideModal(true)
  }, [hideModal])
  const onDecline = useCallback(() => {
    return hideModal(false)
  }, [hideModal])

  return (
    <>
      <div className={confirmationModalTitle}>{title}</div>
      <div className={confirmationModalMessage}>{message}</div>
      <div className={confirmationModalActions}>
        <ButtonComponent
          variation="secondary"
          size="xSmall"
          label={confirmLabel}
          onClick={onConfirm}
        />
        <ButtonComponent
          size="xSmall"
          label={cancelLabel}
          onClick={onDecline}
        />
      </div>
    </>
  )
}
